import numbro from "numbro"
import lodashGet from "lodash/get"
import merge from "lodash/merge";

export const formatSteps = (steps) => numbro(steps).format({thousandSeparated: true})

export const convertToSteps = meters => Math.round(meters / 0.6858)

export const fetchMemberFitnessActivities = async (params) => {
  const { allActivities, page, after, results = [] } = params;
  const query = `
    query getFitness($slug: Slug, $after: String) {
      page(slug: $slug) {
        timeline(first: 20, entryType: FITNESS, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            fitnessActivity {
              steps
            }
          }
        }
      }
    }
  `;

  return request('/v1/justgiving/graphql', {
    method: 'POST',
    body: {
      query,
      variables: { slug: page.includes('fundraising/') ? page : 'fundraising/' + page, after },
    },
  })
    .then((result) => {
      const data = lodashGet(result, "data.page.timeline", {});
      const { pageInfo = {}, nodes = [] } = data;
      const updatedResults = [...results, ...nodes];

      if (allActivities && pageInfo.hasNextPage) {
        return fetchMemberFitnessActivities({
          page,
          after: pageInfo.endCursor,
          results: updatedResults,
          allActivities: true,
        });
      } else {
        return updatedResults
          .filter((activity) => activity.fitnessActivity)
          .map((activity) => ({
            ...activity,
            ...lodashGet(activity, "fitnessActivity", {}),
          }));
        }
    });
}

// const API_BASE_URL = 'https://api.justgiving.com'
const SERVICES_API_BASE_URL = 'https://api.blackbaud.services'

const DEFAULT_HEADERS = {
	accept: 'application/json'
}

const request = (path, { params, headers, method = 'GET', body } = {}) => {
	const url = new URL(`${SERVICES_API_BASE_URL}${path}`)

	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				url.searchParams.set(key, value)
			}
		})
	}

	const fetchOptions = {
		method,
		headers: {
			...DEFAULT_HEADERS,
			...headers
		}
	}

	if (body !== undefined) {
		fetchOptions.headers['content-type'] = 'application/json'
		fetchOptions.body = JSON.stringify(body)
	}

	return fetch(url.toString(), fetchOptions).then(async response => {
		if (!response.ok) {
			throw new Error(`Request failed: ${response.status}`)
		}

		return response.json()
	})
}

const getPaginatedTeamMembers = (team) => {
	if (team.membership?.members?.length < team.membership?.numberOfMembers) {
		const slug = (team.shortName || '').replace('team/', '')

		return request(`/v1/team/${slug}`, {
			params: { nextPageKey: team.pagination?.endCursor }
		}).then((res) => {
			const updatedTeam = {
				...team,
				membership: {
					...team.membership,
					members: [...team.membership.members, ...lodashGet(res, 'membership.members', [])]
				},
				pagination: {
					...lodashGet(res, 'pagination', {})
				}
			}

			return getPaginatedTeamMembers(updatedTeam)
		})
	}

	return Promise.resolve(team)
}

export const fetchTeamBySlug = (slug, options = {}) =>
	request(`/v1/team/${String(slug).replace('team/', '')}`)
		.then((team) => {
			if (options.includePages && team.membership?.members?.length) {
				return getPaginatedTeamMembers(team).then((updatedTeam) => {
					if (options.includeFullPages) {
						const ids = updatedTeam.membership.members
							.map((member) => member.fundraisingPageGuid)
							.filter(Boolean)

						return Promise.all(ids.map((id) => fetchPage(id))).then((members) => ({
							...updatedTeam,
							membership: { ...updatedTeam.membership, members }
						}))
					}

					return updatedTeam
				})
			}

			return team
		})

export const fetchPage = (page, slug, options = {}) => {
	const endpoint = slug || Number.isNaN(Number(page)) ? 'page' : 'page/id'

	const fetchers = [
		request(`/v1/${endpoint}/${page}`).then(pageData => {
			if (!options.includeFitness) {
				return pageData
			}

			return fetchPageFitness(pageData, options.fitnessParams).then(fitness => ({
				...pageData,
				fitness
			}))
		}),
		options.includeTags ? fetchPageTags(page) : Promise.resolve(undefined)
	]

	return Promise.all(fetchers).then(([pageData, tags]) => ({
		...pageData,
		...tags
	}))
}

export const fetchPageTags = page => request(`/v1/page/tags/${page}`)

export const fetchPageFitness = (
	page,
	{ limit = 100, offset = 0, startDate, endDate } = {}
) => {
	const slug = typeof page === 'object' ? page.pageShortName : page
	const params = { limit, offset, start: startDate, end: endDate }

	return request(`/v1/fitness/page/${slug}`, { params }).then(res =>
		request(
			`/v1/justgiving/page/${slug}/fitnessTotal`,
			{ params: { startDate, endDate } }
		).then(data => ({
			...res,
			totalAmount: data.distance,
			totalAmountElevation: data.elevation,
			totalAmountTaken: data.duration
		}))
	)
}

export const deserializeFitnessActivity = activity => activity

export const setLocaleFromCurrency = (currency) => {
  if (typeof currency !== "string") return "en-GB";

  const code = currency.toLowerCase();

  switch (code) {
    case "aud":
      return "en-AU";
    case "ca":
      return "en-CA";
    case "hk":
      return "en-HK";
    case "ie":
    case "eur":
      return "en-IE";
    case "nzd":
      return "en-NZ";
    case "sgd":
      return "en-SG";
    case "usd":
      return "en-US";
    case "zar":
      return "en-ZA";
    default:
      return "en-GB";
  }
};

export const setLocaleFromCountry = (country) => {
  if (typeof country !== "string") return "en-GB";
  return `en-${country.toUpperCase()}`;
};

export const formatNumber = ({
  amount,
  locale = "en-GB",
  notation = "standard",
  places = 0,
  style = "decimal",
  ...options
}) => {
  const config = merge(
    {},
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: Math.min(places, 20),
      notation,
      style,
    },
    options
  );

  return Intl.NumberFormat(locale, config).format(amount);
};

export const formatCurrency = ({
  amount,
  currencyCode: currency,
  currencyDisplay = "symbol",
  locale: baseLocale,
  notation,
  places,
}) => {
  const locale = baseLocale || setLocaleFromCurrency(currency);
  const isWholeNumber = amount % 1 === 0;

  return formatNumber({
    amount,
    currency: currency ? currency.toUpperCase() : currencyCode(locale),
    currencyDisplay,
    locale,
    minimumFractionDigits: places && !isWholeNumber ? 2 : 0,
    maximumFractionDigits: places ? 2 : 0,
    notation,
    places,
    style: "currency",
  });
};

export const currencyCode = (country = "UK") => {
  const countryCode = country.toLowerCase().replace(/(en|cy|fr|ga)-/, "");

  switch (countryCode) {
    case "au":
      return "AUD";
    case "us":
      return "USD";
    case "ie":
      return "EUR";
    case "nz":
      return "NZD";
    case "sg":
      return "SGD";
    case "hk":
      return "HKD";
    case "ca":
      return "CAD";
    case "za":
      return "ZAR";
    default:
      return "GBP";
  }
};

export const currencySymbol = (code = "GBP") => {
  switch (code.toLowerCase()) {
    case "gbp":
      return "£";
    case "hkd":
      return "HK$";
    case "aed":
      return "د.إ";
    case "eur":
      return "€";
    default:
      return "$";
  }
};
